import { useState, useEffect, useCallback } from 'react';
import adminApi from '../../services/adminApi';
import LoadingSpinner from '../../components/LoadingSpinner';
import { AdminModal } from '../../components/AdminModal';
import { apiErrorMessage } from '../../utils/apiError';
import { IoEyeOutline } from 'react-icons/io5';
import AdminDataTable from '../../components/admin/AdminDataTable';
import { useDebouncedValue, useLatestRequest, useFirstLoad } from '../../components/admin/useAdminTable';
import './AdminAuditPage.css';

const EMPTY_FILTERS = { adminUserId: '', action: '', targetType: '', targetId: '' };

const DetailModal = ({ entry, onClose, onShowHistory }) => (
  <AdminModal wide title={entry.action} onClose={onClose} footer={
    <>
      {entry.targetId && (
        <button type="button" className="admin-modal-btn" onClick={() => onShowHistory(entry)}>
          All changes to this {entry.targetType || 'record'}
        </button>
      )}
      <button type="button" className="admin-modal-btn" onClick={onClose}>Close</button>
    </>
  }>
    <ul className="audit-detail-meta">
      <li><strong>When</strong> {new Date(entry.createdAt).toLocaleString()}</li>
      <li><strong>Admin</strong> {entry.adminUser?.email || 'account since deleted'}</li>
      <li><strong>Target</strong> {entry.targetType} {entry.targetId || ''}</li>
      {entry.ipAddress && <li><strong>IP</strong> {entry.ipAddress}</li>}
    </ul>

    <p className="audit-detail-summary">{entry.summary}</p>

    {/* Only the fields that actually changed are stored, and passwords and reset
        tokens are stripped before the write. */}
    <div className="audit-diff">
      <div>
        <h4>Before</h4>
        <pre>{entry.before ? JSON.stringify(entry.before, null, 2) : '—'}</pre>
      </div>
      <div>
        <h4>After</h4>
        <pre>{entry.after ? JSON.stringify(entry.after, null, 2) : '—'}</pre>
      </div>
    </div>
  </AdminModal>
);

const AdminAuditPage = () => {
  const [entries, setEntries] = useState([]);
  const [facets, setFacets] = useState({ actions: [], targetTypes: [], admins: [] });
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewing, setViewing] = useState(null);
  // Free-text filters wait for typing to pause; the dropdowns apply at once
  const debouncedAction = useDebouncedValue(filters.action);
  const debouncedTargetId = useDebouncedValue(filters.targetId.trim());
  const beginRequest = useLatestRequest();
  const firstLoad = useFirstLoad(loading);

  const load = useCallback(async () => {
    const request = beginRequest();
    try {
      setLoading(true);
      const response = await adminApi.getAuditLog({
        page: currentPage,
        limit: 50,
        adminUserId: filters.adminUserId,
        targetType: filters.targetType,
        action: debouncedAction,
        targetId: debouncedTargetId
      });
      if (!request.isCurrent()) return;
      setEntries(response.data.entries || []);
      setPagination(response.data.pagination || {});
      setError(null);
    } catch (err) {
      if (!request.isCurrent()) return;
      setError(apiErrorMessage(err, 'Failed to load the audit log.'));
    } finally {
      if (request.isCurrent()) setLoading(false);
    }
  }, [beginRequest, currentPage, filters.adminUserId, filters.targetType, debouncedAction, debouncedTargetId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    adminApi.getAuditLogFacets()
      .then((response) => setFacets(response.data))
      .catch(() => { /* the dropdowns just stay at "Any"; the log itself still loads */ });
  }, []);

  const setFilter = (field) => (event) => {
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
    setCurrentPage(1);
  };

  const showHistory = (entry) => {
    setViewing(null);
    setFilters({ ...EMPTY_FILTERS, targetType: entry.targetType || '', targetId: entry.targetId });
    setCurrentPage(1);
  };

  if (firstLoad) {
    return (
      <div className="admin-loading">
        <LoadingSpinner size="large" message="Loading audit log..." />
      </div>
    );
  }

  return (
    <div className="admin-audit-page">
      <div className="admin-page-header">
        <h1 className="admin-page-title">Admin Audit Log</h1>
        <p className="admin-page-description">
          Every change an admin made on someone&apos;s behalf. Read-only by design.
        </p>
      </div>

      <div className="admin-card">
        <div className="audit-filters">
          <label>
            <span>Admin</span>
            <select value={filters.adminUserId} onChange={setFilter('adminUserId')}>
              <option value="">Anyone</option>
              {facets.admins.map((admin) => (
                <option key={admin.id} value={admin.id}>{admin.email}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Action contains</span>
            <input
              type="text"
              value={filters.action}
              onChange={setFilter('action')}
              placeholder="e.g. family, delete"
              list="audit-actions"
            />
            <datalist id="audit-actions">
              {facets.actions.map((action) => <option key={action} value={action} />)}
            </datalist>
          </label>
          <label>
            <span>Target type</span>
            <select value={filters.targetType} onChange={setFilter('targetType')}>
              <option value="">Any</option>
              {facets.targetTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Target id</span>
            <input
              type="text"
              value={filters.targetId}
              onChange={setFilter('targetId')}
              placeholder="Record id"
            />
          </label>
          <button className="audit-clear-btn" onClick={() => { setFilters(EMPTY_FILTERS); setCurrentPage(1); }}>
            Clear
          </button>
        </div>
      </div>

      <AdminDataTable
        title={`Entries (${pagination.totalEntries ?? entries.length})`}
        rows={entries}
        getRowLabel={(entry) => entry.action}
        loading={loading}
        error={error}
        onRetry={load}
        emptyTitle="Nothing recorded yet"
        emptyMessage="Admin actions appear here as they happen. An empty log on a busy site can also mean the audit table is missing — check that the migration has run."
        pagination={{
          page: currentPage,
          totalPages: pagination.totalPages,
          hasPrev: pagination.hasPrevPage,
          hasNext: pagination.hasNextPage,
          onPageChange: setCurrentPage
        }}
        columns={[
          { key: 'when', header: 'When', render: (entry) => new Date(entry.createdAt).toLocaleString() },
          { key: 'admin', header: 'Admin', render: (entry) => entry.adminUser?.email || 'deleted account' },
          { key: 'action', header: 'Action', render: (entry) => <code>{entry.action}</code> },
          { key: 'summary', header: 'Summary', render: (entry) => entry.summary }
        ]}
        renderActions={(entry) => (
          <button type="button" onClick={() => setViewing(entry)} title="View before and after">
            <IoEyeOutline /> View
          </button>
        )}
      />

      {viewing && <DetailModal entry={viewing} onClose={() => setViewing(null)} onShowHistory={showHistory} />}
    </div>
  );
};

export default AdminAuditPage;
